import React, { useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import * as Yup from "yup";

import FormBuilder from "../../../../shared/components/form-builder/form-builder";
import { ATBuyerSellerDetails } from "../../atc-models/atc-org";
import useNotification from "../../../../shared/hooks/useNotification";
import { buyerSellerDetailsService } from "../../../v4/transaction-certificates/tc-service/buyer-seller-details.service";

// ------------------ Props ------------------
interface BuyerSellerDetailsFormProps {
  formRef: React.RefObject<any>;
  detailsData: ATBuyerSellerDetails;
  setDetailsData: (data: ATBuyerSellerDetails) => void;
}

// ------------------ Component ------------------
const BuyerSellerDetailsForm: React.FC<BuyerSellerDetailsFormProps> = ({
  formRef,
  detailsData,
  setDetailsData,
}) => {
  // const formRef = useRef<HTMLFormElement>(null);
  const { notify } = useNotification();

  // ------------------ State ------------------
  const [visible, setVisible] = useState({
    sellerName: true,
    buyerName: true,
    sellerTeIdLabel: true, 
    buyerTeIdLabel: true,
  });

  const [disable, setDisable] = useState({
    sellerAddress: true,
    buyerAddress: true,
    buyerScNumber: true,
  });

  const [sellerHasTeid, setSellerHasTeid] = useState<"Yes" | "No">("Yes");

  // ------------------ Validation ------------------
const validationSchema = Yup.object().shape({
  sellerTeId: Yup.string(), // may be text or search based on flag
  sellerAddress: Yup.string().required("Seller Address is required"),
  sellerName: Yup.string().required("Seller Name is required"),
  buyerTeId: Yup.string().required("Buyer TE ID is required"),
  buyerAddress: Yup.string().required("Buyer Address is required"),
  buyerName: Yup.string().required("Buyer Name is required"),
  buyerScNumber: Yup.string().required("Buyer SC Number is required"),
});

  // ------------------ API ------------------
  const fetchTEDetailsFromAPI = async (
    value: string,
    offset: number,
    type: string
  ): Promise<any[]> => {
    try {
      const queryParams: Record<string, any> = {
        query_param: type === "query" ? value : "",
        offset,
        name: type === "name" ? value : "",
      };
      const response = await buyerSellerDetailsService.getTEDetails(
        queryParams
      );
      return response.data || [];
    } catch (error) {
      console.error("Error fetching TE details:", error);
      notify("Failed to fetch TE details", "error");
      return [];
    }
  };

  // ------------------ Seller Handlers ------------------
  const handleSellerTEIdValueChange = (value: any) => {
    if (value == null) {
      // Reset
      [
        "sellerTeId",
        "sellerName",
        "sellerAddress",
      ].forEach((field) => formRef.current?.setFieldValue(field, ""));

      setDisable((prev) => ({
        ...prev,
        sellerName: false,
        sellerAddress: false,
      }));

      setVisible((prev) => ({
        ...prev,
        sellerName: true,
        sellerTeIdLabel: true,
      }));
    } else {
      // Populate
      formRef.current?.setFieldValue("sellerTeId", value.TE_ID);
      formRef.current?.setFieldValue("sellerName", value.account_name);
      formRef.current?.setFieldValue(
        "sellerAddress",
        value.street || ""
      );
      
      setDisable((prev) => ({
        ...prev,
        sellerName: Boolean(value.account_name),
        sellerAddress: Boolean(value.street),
      }));

      setVisible((prev) => ({ ...prev, sellerName: false }));
    }
  };

  const handleSellerNamedValueChange = (value: any) => {
    if (value == null) {
      [
        "sellerAddress",
        "sellerName",
        "sellerTeId",
      ].forEach((field) => formRef.current?.setFieldValue(field, ""));

      setDisable((prev) => ({
        ...prev,
        sellerAddress: false,
      }));
      setVisible((prev) => ({ ...prev, sellerTeIdLabel: true }));
    } else {
      formRef.current?.setFieldValue("sellerName", value.account_name);
      formRef.current?.setFieldValue("sellerTeId", value.TE_ID);

      // Address
      if (value.street) {
        formRef.current?.setFieldValue("sellerAddress", value.street);
        setDisable((prev) => ({ ...prev, sellerAddress: true }));
      } else {
        formRef.current?.setFieldValue("sellerAddress", "");
        setDisable((prev) => ({ ...prev, sellerAddress: false }));
      }

      // // Licensing Code
      // if (value.cb_licensing_code) {
      //   formRef.current?.setFieldValue(
      //     "sellerCbLicensingCode",
      //     value.cb_licensing_code
      //   );
      //   setDisable((prev) => ({ ...prev, sellerCbLicensingCode: true }));
      // } else {
      //   formRef.current?.setFieldValue("sellerCbLicensingCode", "");
      //   setDisable((prev) => ({ ...prev, sellerCbLicensingCode: false }));
      // }

      setVisible((prev) => ({ ...prev, sellerTeIdLabel: false }));
    }
  };

  // ------------------ Buyer Handlers ------------------
  const handleBuyerTEIdValueChange = (value: any) => {
    if (value == null) {
      ["buyerTeId", "buyerName", "buyerAddress", "buyerScNumber"].forEach(
        (field) => formRef.current?.setFieldValue(field, "")
      );

      setDisable((prev) => ({
        ...prev,
        buyerName: false,
        buyerAddress: false,
        buyerScNumber: false,
      }));
      setVisible((prev) => ({
        ...prev,
        buyerName: true,
        buyerTeIdLabel: true,
      }));
    } else {
      formRef.current?.setFieldValue("buyerTeId", value.TE_ID);
      formRef.current?.setFieldValue("buyerName", value.account_name);
      formRef.current?.setFieldValue(
        "buyerAddress",
        value.street || ""
      );
      formRef.current?.setFieldValue(
        "buyerScNumber",
        value.sc_number || ""
      );

      setDisable((prev) => ({
        ...prev,
        buyerName: Boolean(value.account_name),
        buyerAddress: Boolean(value.street),
        buyerScNumber: false, // always editable
      }));

      setVisible((prev) => ({ ...prev, buyerName: false }));
    }
  };

  const handleBuyerNameValueChange = (value: any) => {
    if (value == null) {
      ["buyerAddress", "buyerName", "buyerTeId", "buyerScNumber"].forEach(
        (field) => formRef.current?.setFieldValue(field, "")
      );

      setDisable((prev) => ({
        ...prev,
        buyerAddress: false,
        buyerScNumber: false,
      }));
      setVisible((prev) => ({ ...prev, buyerTeIdLabel: true }));
    } else {
      formRef.current?.setFieldValue("buyerName", value.account_name);
      formRef.current?.setFieldValue("buyerTeId", value.TE_ID);

      // Address
      if (value.street) {
        formRef.current?.setFieldValue("buyerAddress", value.street);
        setDisable((prev) => ({ ...prev, buyerAddress: true }));
      } else {
        formRef.current?.setFieldValue("buyerAddress", "");
        setDisable((prev) => ({ ...prev, buyerAddress: false }));
      }

      // SC Number
      if (value.sc_number) {
        formRef.current?.setFieldValue(
          "buyerScNumber",
          value.sc_number
        );
        setDisable((prev) => ({ ...prev, buyerScNumber: true }));
      } else {
        formRef.current?.setFieldValue("buyerScNumber", "");
        setDisable((prev) => ({ ...prev, buyerScNumber: false }));
      }

      setVisible((prev) => ({ ...prev, buyerTeIdLabel: false }));
    }
  };

  // ------------------ Form Config ------------------
  const formConfig = {
    pageName: "buyer-seller-details",
    initialValues: { ...detailsData, sellerHasTeid: "Yes" },
    validationSchema,
    enableReinitialize: true,
    sections: [
      {
        title: "Seller Details",
        fields: [
          {
            label: "Does Seller have a TE-ID?",
            name: "sellerHasTeid",
            type: "radio",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: sellerHasTeid,
            props: {
              options: [
                { label: "Yes", value: "Yes" },
                { label: "No", value: "No" },
              ],
              onChange: (e: any) => {
                const v = e.target.value as "Yes" | "No";
                setSellerHasTeid(v);
                // keep existing form values; no reinitialize
                if (formRef.current?.setFieldValue) {
                  formRef.current.setFieldValue("sellerHasTeid", v);
                }
              },
            },
          },
          // Seller TE ID (Search when Yes)
          {
            label: "Seller TE-ID",
            name: "sellerTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            value: detailsData.sellerTeId,
            visible: () => sellerHasTeid === "Yes",
            props: {
              placeholder: "Search Seller TE-ID",
              variant: "outlined",
              restrictInputFormat: true,
              param: "SellerTEId",
              onChange: handleSellerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "query"),
              fullWidth: true,
            },
          },
          // Seller TE-ID (free text when No)
          {
            label: "Seller TE-ID",
            name: "sellerTeId",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: false,
            disabled: true,
            visible: () => sellerHasTeid === "No",
            props: {
              placeholder: "Enter Seller TE-ID",
              variant: "outlined",
              inputProps: { maxLength: 11 },
              fullWidth: true,
            },
          },
          // Seller Name (search when Yes)
          {
            label: "Seller Name",
            name: "sellerName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerName,
            visible: () => sellerHasTeid === "Yes" && visible.sellerName,
            props: {
              placeholder: "Search Seller Name",
              variant: "outlined",
              onChange: handleSellerNamedValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
              fullWidth: true,
            },
          },
          // Seller Name (read-only after lookup, for Yes flow)
          {
            label: "Seller Name",
            name: "sellerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: true,
            visible: () => sellerHasTeid === "Yes" && !visible.sellerName,
            props: {
              placeholder: "Enter Seller Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Seller Name (editable text when No)
          {
            label: "Seller Name",
            name: "sellerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: false,
            visible: () => sellerHasTeid === "No",
            props: {
              placeholder: "Enter Seller Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Seller Address (read-only when Yes + lookup, editable when No)
          {
            label: "Seller Address",
            name: "sellerAddress",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.sellerAddress,
            // 🔑 key change: address is only disabled in the “Yes + auto-fill” flow
            disabled: sellerHasTeid === "Yes" && disable.sellerAddress,
            props: {
              placeholder: "Enter Seller Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
        ],
      },
      {
        title: "Buyer Details",
        fields: [
          // Buyer TE ID (Search with clear)
          {
            label: "Buyer TE-ID",
            name: "buyerTeId",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerTeId,
            visible: () => true,
            props: {
              placeholder: "Search Buyer TE-ID",
              variant: "outlined",
              restrictInputFormat: true,
              onChange: handleBuyerTEIdValueChange,
              displayKey: "TE_ID",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "query"),
              fullWidth: true,
            },
          },
          // Buyer Name (Search / Text)
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "search",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerName,
            visible: () => visible.buyerName,
            props: {
              placeholder: "Search Buyer Name",
              variant: "outlined",
              onChange: handleBuyerNameValueChange,
              displayKey: "account_name",
              onSearch: (query: string, offset: number) =>
                fetchTEDetailsFromAPI(query, offset, "name"),
              fullWidth: true,
            },
          },
          {
            label: "Buyer Name",
            name: "buyerName",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            disabled: true,
            visible: () => !visible.buyerName,
            props: {
              placeholder: "Enter Buyer Name",
              variant: "outlined",
              fullWidth: true,
            },
          },
          // Address + SC Number
          {
            label: "Buyer Address",
            name: "buyerAddress",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerAddress,
            disabled: disable.buyerAddress,
            props: {
              placeholder: "Enter Buyer Address",
              variant: "outlined",
              fullWidth: true,
            },
          },
          {
            label: "Buyer SC Number",
            name: "buyerScNumber",
            type: "text",
            gridSize: { xs: 12, md: 6, lg: 6 },
            required: true,
            value: detailsData.buyerScNumber,
            disabled: false,
            props: {
              placeholder: "Enter SC Number",
              variant: "outlined",
              fullWidth: true,
            },
          },
        ],
      },
    ],
    handleSubmit: async (values: any) => {setDetailsData(values);
      
      const errors = (await formRef.current?.validateForm()) ?? {};
      if (Object.keys(errors || {}).length > 0) {
        notify("Please fill all the required fields", "error");
        return;
      }
    },
  };

  // ------------------ Render ------------------
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 6, fontWeight: "bold" }}>
        Buyer & Seller Details
      </Typography>

      <FormBuilder
        formConfig={formConfig}
        onSubmit={formConfig.handleSubmit}
        formRef={formRef}
      />
    </Box>
  );
};

export default BuyerSellerDetailsForm;
